import type {ChangeEvent, SyntheticEvent} from "react";
import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import classNames from "classnames";
import useInfiniteScroll from "react-infinite-scroll-hook";

import colors from "styles/themes.module.scss";
import {DriveFolderUploadIcon, RecycleBin} from "assets/icons";
import {useAppDispatch, useAppSelector, useModalHook, useSearchDebounce} from "hooks";
import {AutoComplete} from "mapx-components";
import GeneralButton from "mapx-components/Buttons/GeneralButton";
import {
	CompanyItem,
	Message,
	ModalHeader,
	ModalSeparator,
	SelectWithTooltip,
	Switch,
	TextInput,
} from "components";
import {searchedCompaniesInProgressSelector} from "store/filter/filterSelectors";
import {clearSearchedCompanies} from "store/filter/filterActions";
import {EMPTY_COMPANY_LOGO} from "mapx-constants";
import {createMarketMap} from "store/market-map/marketMapAsyncFunctions";
import {
	createMarketMapModalPropsSelector,
	createMarketMapModalSelector,
	creationErrorSelector,
	creationInProgressSelector,
} from "store/market-map/marketMapSelectors";
import {createMarketMapModal, resetMarketMapError} from "store/market-map/marketMapActions";
import type {TRootState} from "types";
import type {STCompany} from "api/companyApi/types";

import css from "./createMarketMapModal.module.scss";
import ImportFromProjectModal from "../ImportFromProjectModal";
import ImportFromTargetListModal from "../ImportFromTargetListModal";
import {isSuperUserSelector} from "store/user/userSelectors";
import {getSearchedCompaniesData} from "store/filter/CompanySearchPageFilters/CompanyFilterAsyncActions";
import {TCreateMarketMapModalProps} from "store/market-map/types";
import Loader from "components/Plugins/Loader";
import mapXTargetListApi from "api/targetListApi";
import {successResponse} from "helpers/map";
import {targetListIDSelector} from "store/target-list/targetListSelectors";
import {TFilterReducerState} from "store/filter/types";
import {ATPagination} from "api/types";

const CreateMarketMapModal = () => {
	const dispatch = useAppDispatch();

	const props: TCreateMarketMapModalProps = useSelector(createMarketMapModalPropsSelector);

	const modalDisplay = useSelector(createMarketMapModalSelector);

	const [name, setName] = useState("");

	const [description, setDescription] = useState("");

	const [companies, setCompanies] = useState<STCompany[]>([]);

	const [global, setGlobal] = useState(false);

	const [showImportFromProjectModal, setShowImportFromProjectModal] = useState(false);

	const [showImportFromTargetListModal, setShowImportFromTargetListModal] = useState(false);

	const [fetchingAllCompanies, setFetchingAllCompanies] = useState(false);

	const [validation, setValidation] = useState({
		field: "",
		message: "",
	});

	const max500 = companies.length > 500;

	const toggleGlobal = useCallback(() => setGlobal(!global), [global]);

	const handleModalDisplay = useCallback(() => {
		dispatch(createMarketMapModal(false, {}));
	}, [dispatch]);

	const {modalIsOpen, customStyles, Modal} = useModalHook(
		{content: {width: "640px", borderRadius: "8px", overflow: "visible"}},
		modalDisplay,
		colors.mainThemeColor,
	);

	const [searchTerm, setSearchTerm] = useSearchDebounce(800);

	const searchedCompanies = useAppSelector(
		(state: TRootState) => (state.filter as TFilterReducerState).searchedCompanies,
	);

	const loading = useAppSelector(searchedCompaniesInProgressSelector);

	const submitting = useAppSelector(creationInProgressSelector);

	const error = useAppSelector(creationErrorSelector);

	const isSuperUser = useAppSelector(isSuperUserSelector);

	const targetListId = useAppSelector(targetListIDSelector);

	const get500TLCompanies = useCallback(async () => {
		const apiPayload = {
			filters: {
				target_list: targetListId,
			},
			pagination: {
				page: 1,
				per_page: 500,
			},
		};

		const response = await mapXTargetListApi.getTargetListCompanies(apiPayload);

		if (successResponse(response, 200)) {
			const data = response.data;

			if (data?.results) {
				// setCompanies(data.results);

				return data.results;
			}
		}

		return [];
	}, [targetListId]);

	useEffect(() => {
		if (props.companies && Array.isArray(props.companies)) {
			setCompanies(props.companies);

			if (props.total_count) {
				if (props.total_count > props.companies.length) {
					setFetchingAllCompanies(true);
					get500TLCompanies()
						.then((companies) => setCompanies(companies))
						.finally(() => setFetchingAllCompanies(false));
				}
			}
		}
	}, [props, get500TLCompanies]);

	const searchCompanies = useCallback(
		(companyName: string, page: number) => {
			if (companyName && companyName.length >= 2) {
				dispatch(getSearchedCompaniesData(companyName, page));
			}
		},
		[dispatch],
	);

	const handleImportCompanies = useCallback(
		(importedCompanies: STCompany[]) => {
			const existingIds = new Set(companies.map((company) => company.id));

			setCompanies([
				...importedCompanies.filter((company) => !existingIds.has(company.id)),
				...companies,
			]);
		},
		[companies],
	);

	const [loadingRef] = useInfiniteScroll({
		loading,
		hasNextPage:
			(searchedCompanies?.pagination as ATPagination)?.pages >
			(searchedCompanies?.pagination as ATPagination)?.page,
		onLoadMore: () =>
			searchCompanies(
				searchTerm,
				(searchedCompanies?.pagination as ATPagination)?.page + 1 || 1,
			),
		rootMargin: "0px 0px 400px 0px",
	});

	const handleItemClick = useCallback(
		(item: STCompany) => {
			// Do not add if already exists
			if (companies.find((company) => company.id === item.id)) return;

			setCompanies([item, ...companies]);
		},
		[companies],
	);

	const handleItemRemove = useCallback(
		(item: STCompany) => {
			setCompanies(companies.filter((company) => company.id !== item.id));
		},
		[companies],
	);

	const handleSubmit = useCallback(
		(e: SyntheticEvent) => {
			e.preventDefault();

			if (!name.trim()) {
				setValidation({
					message: "Please enter a valid name for the target List.",
					field: "name",
				});

				return;
			}

			dispatch(
				createMarketMap(
					name.trim(),
					global,
					companies.map((company) => company.id),
					description.trim(),
				),
			);
		},
		[companies, description, dispatch, global, name],
	);

	const handleNameChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setName(e.target.value);

			if (error?.status === 409) {
				dispatch(resetMarketMapError());
			}
			if (validation.field === "name") {
				setValidation({
					message: "",
					field: "",
				});
			}
		},
		[dispatch, error, validation],
	);

	const handleDescriptionChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setDescription(e.target.value);
	}, []);

	const handleImportFromTargetList = useCallback(async () => {
		setFetchingAllCompanies(true);
		await get500TLCompanies().then((results) => {
			handleImportCompanies(results);
		});
		setFetchingAllCompanies(false);
	}, [handleImportCompanies, get500TLCompanies]);

	const handleRemoveAllCompanies = useCallback(() => {
		setCompanies([]);
	}, []);

	useEffect(() => {
		dispatch(clearSearchedCompanies());

		if (searchTerm) {
			searchCompanies(searchTerm, 1);
		}
	}, [searchTerm, dispatch, searchCompanies]);

	const InlineLoaderComponent = (
		<Loader
			height={40}
			width={40}
			customStyle={{display: "flex", justifyContent: "center"}}
			type="ThreeDots"
		/>
	);

	return (
		<>
			<Modal
				title="Create a Target List"
				style={customStyles}
				isOpen={modalIsOpen}
				contentLabel="Modal"
				onRequestClose={handleModalDisplay}
			>
				<ModalHeader title="Create a Target List" onClose={handleModalDisplay} />
				<ModalSeparator />
				<form className={css.content} onSubmit={handleSubmit}>
					<TextInput
						label="Name"
						maxLength={255}
						name="name"
						onChange={handleNameChange}
						placeholder="Type a name"
						required
						type="text"
						value={name}
						errorText={
							error?.status === 409
								? error.message
								: validation.field === "name"
								? validation.message
								: undefined
						}
					/>
					<TextInput
						customClass={css.description}
						label="Description"
						maxLength={255}
						name="description"
						placeholder="Type a description"
						type="text"
						value={description}
						onChange={handleDescriptionChange}
					/>
					{isSuperUser && (
						<>
							<label className={css.label}>Share as MapX</label>
							<Switch checked={global} onChange={toggleGlobal} />
							<span className={css.global}>{global ? "Active" : "Inactive"}</span>
						</>
					)}
					<div className={css.line} />
					<div className={css.row}>
						<AutoComplete
							handleFilterChange={setSearchTerm}
							handleItemClick={handleItemClick}
							hasMore={loading}
							isLoading={loading}
							ref={loadingRef}
							notFoundResult="No company found"
							SuggestionListItemComponent={CompanyItem}
							suggestions={searchedCompanies.results}
						/>
						<SelectWithTooltip
							className={css.select}
							placeholder="Import"
							icon={<DriveFolderUploadIcon width={24} height={24} />}
							options={[
								{
									label: "From Target List",
									action: handleImportFromTargetList,
								},
								{
									label: "From Project",
									action: () => setShowImportFromProjectModal(true),
								},
							]}
						/>
					</div>
					{max500 && (
						<Message
							message="You can add max 500 companies per Target List."
							className={css.message}
							level="error"
						/>
					)}
					<div className={css.companies}>
						<div className={classNames(css.title, css.label)}>
							Added Companies
							<div className={css.count}>{companies.length}</div>
							<div className={css.removeAll} onClick={handleRemoveAllCompanies}>
								<RecycleBin />
								Remove all
							</div>
						</div>

						{!fetchingAllCompanies &&
							(companies.length > 0 ? (
								<div className={css.list}>
									{companies.map((company) => (
										<div key={company.id.toString()} className={css.company}>
											<img
												src={company.logo_url || EMPTY_COMPANY_LOGO}
												onError={(e) => {
													e.currentTarget.src = EMPTY_COMPANY_LOGO;
												}}
												className={css.logo}
												alt={company.name}
											/>
											<div className={css.name}>{company.name}</div>
											<div
												className={css.delete}
												onClick={() => handleItemRemove(company)}
											>
												<RecycleBin />
											</div>
										</div>
									))}
								</div>
							) : (
								<div className={css.empty}>No companies added</div>
							))}

						{fetchingAllCompanies && InlineLoaderComponent}
					</div>
					<GeneralButton
						disabled={max500 || companies.length === 0}
						customClass={css.button}
						loadingSave={submitting}
						title="Create Target List"
					/>
				</form>
			</Modal>
			<ImportFromProjectModal
				modalDisplay={showImportFromProjectModal}
				setModalDisplay={setShowImportFromProjectModal}
				setCompanies={handleImportCompanies}
			/>
			<ImportFromTargetListModal
				modalDisplay={showImportFromTargetListModal}
				setModalDisplay={setShowImportFromTargetListModal}
				setCompanies={handleImportCompanies}
			/>
		</>
	);
};

export default CreateMarketMapModal;
